/**
 * SecureEmbed Energy Portal - InfoTooltip Component
 *
 * Click-to-open info popover explaining what a figure is, how it was derived, and its significance.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Info } from 'lucide-react';
import { TOOLTIPS, TooltipContent } from '../constants/tooltips';

interface InfoTooltipProps {
  /** Direct content object */
  content?: TooltipContent;
  /** Lookup key into the TOOLTIPS registry */
  tooltipId?: string;
}

const InfoTooltip: React.FC<InfoTooltipProps> = ({ content, tooltipId }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: boolean; left: boolean }>({ top: false, left: false });
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const data = content || (tooltipId ? TOOLTIPS[tooltipId] : undefined);
  if (!data) return null;

  const reposition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.right;
    setPosition({
      top: spaceBelow < 260,
      left: spaceRight < 360,
    });
  }, []);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) reposition();
    setOpen(prev => !prev);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <span className="relative inline-flex items-center print:hidden">
      <button
        ref={btnRef}
        onClick={handleToggle}
        className={`p-0.5 rounded transition-colors focus:outline-none ${
          open ? 'text-purple-600' : 'text-gray-400 hover:text-gray-600'
        }`}
        aria-label="More information"
        type="button"
      >
        <Info className="w-4 h-4" />
      </button>

      {open && (
        <div
          ref={panelRef}
          className={`absolute z-50 w-[340px] bg-white border border-gray-200 shadow-lg rounded-xl p-4 transition-opacity duration-150 ${
            position.top ? 'bottom-full mb-2' : 'top-full mt-2'
          } ${
            position.left ? 'right-0' : 'left-0'
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-1">What</p>
              <p className="text-sm text-gray-700">{data.what}</p>
            </div>
            <div className="border-t border-gray-100" />
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-1">How it&apos;s calculated</p>
              <p className="text-sm text-gray-700">{data.how}</p>
            </div>
            <div className="border-t border-gray-100" />
            <div>
              <p className="text-xs font-semibold uppercase text-gray-400 mb-1">Why it matters</p>
              <p className="text-sm text-gray-700">{data.significance}</p>
            </div>
          </div>
        </div>
      )}
    </span>
  );
};

export default InfoTooltip;
